import React, { useState, useEffect, useCallback } from 'react';
import { BankAccounts, TransactionHistory } from '@/features/home';
import { errorBoundary } from '@/hooks/withErrorBoundary';
import ExportModal from '@/features/home/components/ExportModal';
import { TWalletData } from '@/features/home/types/WalletData';
import useHomeContext from '@/hooks/useHomeContext';
import { useOutletContext } from 'react-router-dom';
import { TOutletContext } from '@/features/home/types/OutletContext';
import { logAnalyticsEvent } from '@/services/firebase';
import { routes } from '@/utils/routes';
import TransactionDetailsDrawer from '@/features/home/components/TransactionDetailsDrawer';
import Breadcrumbs from '@/components/Breadcrumbs';
import { useBreadcrumbs } from '@/features/home/hooks/useBreadcrumbs';
import { isFeatureEnabled } from '@/config/featureFlags.service';
import { Feature } from '@/config/featureFlags.definition';
import { ALL_ACCOUNTS } from '@/context/HomeProvider/HomeContextProvider';

const EmergeHome: React.FC = () => {
  const { accountId } = useOutletContext<TOutletContext>();
  const {
    setSelectedAccount,
    setBankAccount,
    wallets,
    allAccounts,
    defaultOption,
    selectedAccount
  } = useHomeContext()!;
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const { homeBreadcrumb } = useBreadcrumbs();

  const isViewingAllAccounts = selectedAccount.value === ALL_ACCOUNTS;

  const accountBreadcrumbPaths = [
    ...homeBreadcrumb,
    { label: selectedAccount.label, path: undefined }
  ];

  useEffect(() => {
    sessionStorage.removeItem('keepSearchParams');
    if (accountId) {
      logAnalyticsEvent('page_view', {
        client_id: accountId,
        page_location: routes.home,
        page_title: 'web_home'
      });
    }
  }, [accountId]);

  const handleOnSelectBankAccount = useCallback(
    (selected?: TWalletData) => {
      if (selected) {
        setSelectedAccount({
          label: selected?.name,
          labelDescription: selected?.balance!.toString(),
          value: selected?.name
        });
        setBankAccount(selected);
        return;
      }

      if (wallets.length > 1) {
        setBankAccount(allAccounts);
        setSelectedAccount(defaultOption);
      } else {
        setBankAccount(wallets[0]);
        setSelectedAccount({
          label: wallets[0].name,
          labelDescription: wallets[0].balance!.toString(),
          value: wallets[0].name
        });
      }
    },
    [wallets, allAccounts, defaultOption]
  );

  return (
    <>
      {!isViewingAllAccounts && isFeatureEnabled(Feature.SELECTED_ACCOUNT) && (
        <div className="px-4 pt-4">
          <Breadcrumbs paths={accountBreadcrumbPaths} />
        </div>
      )}
      <div className="p-4 py-6 flex flex-col gap-y-4 bg-aux-lightgray">
        {!isViewingAllAccounts && !isFeatureEnabled(Feature.SELECTED_ACCOUNT) && (
          <Breadcrumbs paths={homeBreadcrumb} />
        )}
        <TransactionDetailsDrawer />
        <ExportModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen((prev) => !prev)}
        />
        <div className="bg-emerge-base-3">
          <div>
            <BankAccounts />
          </div>
        </div>
      </div>
      <div>
        <div className="p-4 pb-6 flex flex-col gap-y-4 bg-white">
          <div>
            <TransactionHistory
              onSelectBankAccount={handleOnSelectBankAccount}
              onOpenExport={() => setIsExportModalOpen(true)}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default errorBoundary(EmergeHome);
